/**
 * AdminCategories — manage product categories (slug, display name, image, sort order).
 * Category `id` is the slug stored on product.category — must match exactly.
 */
import { useCallback, useEffect, useState } from 'react';
import api from '../../services/api';
import { toast } from 'sonner';
import { Plus, Trash2, Save, Upload, GripVertical } from 'lucide-react';

const getAuthHeaders = () => ({ Authorization: `Bearer ${sessionStorage.getItem('adminToken')}` });

const resolveImg = (u) => {
  if (!u) return '';
  if (u.startsWith('http') || u.startsWith('data:')) return u;
  if (u.startsWith('/')) return `${process.env.REACT_APP_BACKEND_URL}${u}`;
  return u;
};

const AdminCategories = ({ onChange }) => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newCat, setNewCat] = useState({ id: '', name: '', sort_order: 0 });
  const [saving, setSaving] = useState(false);

  const fetchCategories = useCallback(async () => {
    try {
      const res = await api.get('/admin/product-categories', { headers: getAuthHeaders() });
      setCategories(res.data || []);
      if (onChange) onChange();
    } catch {
      toast.error('Kunde inte hämta kategorier');
    } finally {
      setLoading(false);
    }
  }, [onChange]);

  useEffect(() => { fetchCategories(); }, [fetchCategories]);

  const handleCreate = async () => {
    if (!newCat.id.trim() || !newCat.name.trim()) {
      toast.error('Både ID och namn krävs');
      return;
    }
    setSaving(true);
    try {
      await api.post('/admin/product-categories', newCat, { headers: getAuthHeaders() });
      setNewCat({ id: '', name: '', sort_order: 0 });
      await fetchCategories();
      toast.success('Kategori skapad');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Kunde inte skapa');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async (id, patch) => {
    try {
      await api.put(`/admin/product-categories/${id}`, patch, { headers: getAuthHeaders() });
      setCategories((prev) => prev.map((c) => (c.id === id ? { ...c, ...patch } : c)));
    } catch {
      toast.error('Kunde inte uppdatera');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(`Radera kategorin "${id}"?`)) return;
    try {
      await api.delete(`/admin/product-categories/${id}`, { headers: getAuthHeaders() });
      await fetchCategories();
      toast.success('Kategori raderad');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Kunde inte radera');
    }
  };

  const handleImageUpload = async (id, file) => {
    if (!file) return;
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await api.post('/upload', fd, {
        headers: { ...getAuthHeaders(), 'Content-Type': 'multipart/form-data' },
      });
      await handleUpdate(id, { image: res.data.url });
      toast.success('Bild uppladdad');
    } catch {
      toast.error('Kunde inte ladda upp bilden');
    }
  };

  if (loading) return <p className="text-sm text-slate-400">Laddar kategorier...</p>;

  return (
    <div className="space-y-4" data-testid="admin-categories">
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800">
        💡 <strong>Kategori-ID</strong> är slug:en som lagras på varje produkt (t.ex. "mugg", "poster"). När du lägger till en kategori här, välj samma ID när du skapar/redigerar en produkt. Bocka i <strong>Göm i header/footer</strong> för att dölja kategorin från navigationen.
      </div>

      {/* Existing categories */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b">
            <tr>
              <th className="px-3 py-2 text-left font-medium text-slate-600">Sort</th>
              <th className="px-3 py-2 text-left font-medium text-slate-600">Bild</th>
              <th className="px-3 py-2 text-left font-medium text-slate-600">ID (slug)</th>
              <th className="px-3 py-2 text-left font-medium text-slate-600">Visningsnamn</th>
              <th className="px-3 py-2 text-center font-medium text-slate-600">Göm i header</th>
              <th className="px-3 py-2 text-center font-medium text-slate-600">Göm i footer</th>
              <th className="px-3 py-2 text-center font-medium text-slate-600">Göm i produktlista</th>
              <th className="px-3 py-2 text-right font-medium text-slate-600">Åtgärd</th>
            </tr>
          </thead>
          <tbody>
            {categories.map((cat) => (
              <tr key={cat.id} className="border-b last:border-0" data-testid={`category-row-${cat.id}`}>
                <td className="px-3 py-2">
                  <input
                    type="number" min="0" step="1"
                    value={cat.sort_order ?? 0}
                    onChange={(e) => setCategories((prev) => prev.map((c) => (c.id === cat.id ? { ...c, sort_order: parseInt(e.target.value, 10) || 0 } : c)))}
                    onBlur={(e) => handleUpdate(cat.id, { sort_order: parseInt(e.target.value, 10) || 0 })}
                    className="w-14 px-2 py-1 border border-slate-200 rounded text-xs"
                  />
                </td>
                <td className="px-3 py-2">
                  <label className="block w-12 h-12 rounded-md border-2 border-dashed border-slate-300 hover:border-[#2a9d8f] cursor-pointer overflow-hidden bg-slate-50 group">
                    {cat.image ? (
                      <img src={resolveImg(cat.image)} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-slate-400">
                        <Upload className="w-3 h-3" />
                      </div>
                    )}
                    <input
                      type="file" accept="image/*" className="hidden"
                      onChange={(e) => handleImageUpload(cat.id, e.target.files?.[0])}
                    />
                  </label>
                </td>
                <td className="px-3 py-2 font-mono text-xs text-slate-500">{cat.id}</td>
                <td className="px-3 py-2">
                  <input
                    type="text"
                    value={cat.name || ''}
                    onChange={(e) => setCategories((prev) => prev.map((c) => (c.id === cat.id ? { ...c, name: e.target.value } : c)))}
                    onBlur={(e) => handleUpdate(cat.id, { name: e.target.value })}
                    className="w-full px-2 py-1 border border-slate-200 rounded text-sm"
                    data-testid={`category-name-${cat.id}`}
                  />
                </td>
                <td className="px-3 py-2 text-center">
                  <input
                    type="checkbox"
                    checked={!!cat.hidden_in_header}
                    onChange={(e) => {
                      const val = e.target.checked;
                      setCategories((prev) => prev.map((c) => (c.id === cat.id ? { ...c, hidden_in_header: val } : c)));
                      handleUpdate(cat.id, { hidden_in_header: val });
                    }}
                    className="w-4 h-4 accent-[#2a9d8f] cursor-pointer"
                    data-testid={`category-hidden-header-${cat.id}`}
                  />
                </td>
                <td className="px-3 py-2 text-center">
                  <input
                    type="checkbox"
                    checked={!!cat.hidden_in_footer}
                    onChange={(e) => {
                      const val = e.target.checked;
                      setCategories((prev) => prev.map((c) => (c.id === cat.id ? { ...c, hidden_in_footer: val } : c)));
                      handleUpdate(cat.id, { hidden_in_footer: val });
                    }}
                    className="w-4 h-4 accent-[#2a9d8f] cursor-pointer"
                    data-testid={`category-hidden-footer-${cat.id}`}
                  />
                </td>
                <td className="px-3 py-2 text-center">
                  <input
                    type="checkbox"
                    checked={!!cat.hidden_in_products}
                    onChange={(e) => {
                      const val = e.target.checked;
                      setCategories((prev) => prev.map((c) => (c.id === cat.id ? { ...c, hidden_in_products: val } : c)));
                      handleUpdate(cat.id, { hidden_in_products: val });
                    }}
                    className="w-4 h-4 accent-[#2a9d8f] cursor-pointer"
                    data-testid={`category-hidden-products-${cat.id}`}
                  />
                </td>
                <td className="px-3 py-2 text-right">
                  <button
                    onClick={() => handleDelete(cat.id)}
                    className="p-1.5 text-slate-400 hover:text-red-500 rounded hover:bg-red-50"
                    data-testid={`category-delete-${cat.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </td>
              </tr>
            ))}
            {categories.length === 0 && (
              <tr>
                <td colSpan={8} className="px-3 py-6 text-center text-sm text-slate-400 italic">
                  Inga kategorier ännu. Lägg till en nedan.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Create new */}
      <div className="bg-white rounded-xl border border-slate-200 p-4 space-y-3" data-testid="category-create-form">
        <h3 className="text-sm font-semibold text-slate-800 flex items-center gap-2">
          <Plus className="w-4 h-4 text-[#2a9d8f]" /> Lägg till ny kategori
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">ID (slug)</label>
            <input
              type="text"
              value={newCat.id}
              onChange={(e) => setNewCat({ ...newCat, id: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '') })}
              placeholder="t.ex. visitkort"
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm font-mono"
              data-testid="new-category-id-input"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Visningsnamn</label>
            <input
              type="text"
              value={newCat.name}
              onChange={(e) => setNewCat({ ...newCat, name: e.target.value })}
              placeholder="t.ex. Visitkort"
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
              data-testid="new-category-name-input"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Sortering</label>
            <input
              type="number" min="0" step="1"
              value={newCat.sort_order}
              onChange={(e) => setNewCat({ ...newCat, sort_order: parseInt(e.target.value, 10) || 0 })}
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
            />
          </div>
        </div>
        <button
          onClick={handleCreate}
          disabled={saving || !newCat.id.trim() || !newCat.name.trim()}
          className="px-4 py-2 text-sm font-semibold text-white bg-[#2a9d8f] rounded-lg hover:bg-[#268b7e] disabled:opacity-50 flex items-center gap-1.5"
          data-testid="create-category-btn"
        >
          <Save className="w-4 h-4" /> {saving ? 'Skapar...' : 'Skapa kategori'}
        </button>
      </div>
    </div>
  );
};

export default AdminCategories;
